/**
 * RadarChart — pure SVG spider / radar chart.
 *
 * No external charting library required.  Accepts N axes (typically 7) with
 * values in [0, 1].  Styled with Tailwind colour tokens via `currentColor`.
 */

export interface RadarDatum {
  key: string;
  /** Short label displayed on the axis tip. */
  label: string;
  /** Score in [0, 1]. */
  value: number;
}

interface RadarChartProps {
  data: RadarDatum[];
  /** Outer size in px (square).  Default 220. */
  size?: number;
  className?: string;
}

function toRad(i: number, n: number): number {
  return (Math.PI * 2 * i) / n - Math.PI / 2;
}

function ptAt(
  cx: number,
  cy: number,
  r: number,
  i: number,
  n: number,
  value: number,
): { x: number; y: number } {
  const a = toRad(i, n);
  return { x: cx + r * value * Math.cos(a), y: cy + r * value * Math.sin(a) };
}

function polyPoints(pts: { x: number; y: number }[]): string {
  return pts.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');
}

const RINGS = [0.25, 0.5, 0.75, 1.0];

export function RadarChart({ data, size = 220, className }: RadarChartProps) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.34; // polygon radius (fraction of half-size)
  const labelOffset = r + 22; // extra px from centre for labels
  const N = data.length;

  const outerPts = data.map((_, i) => ptAt(cx, cy, r, i, N, 1));
  const scorePts = data.map((d, i) => ptAt(cx, cy, r, i, N, Math.max(d.value, 0.05)));

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={className}
      aria-hidden="true"
    >
      {/* ── Background rings ── */}
      {RINGS.map((v) => (
        <polygon
          key={v}
          points={polyPoints(data.map((_, i) => ptAt(cx, cy, r, i, N, v)))}
          fill="none"
          stroke="currentColor"
          strokeOpacity={0.1}
          strokeWidth={1}
        />
      ))}

      {/* ── Axis lines ── */}
      {outerPts.map((p, i) => (
        <line
          key={i}
          x1={cx}
          y1={cy}
          x2={p.x}
          y2={p.y}
          stroke="currentColor"
          strokeOpacity={0.13}
          strokeWidth={1}
        />
      ))}

      {/* ── Score polygon (fill + stroke) ── */}
      <polygon
        points={polyPoints(scorePts)}
        fill="currentColor"
        fillOpacity={0.13}
        stroke="currentColor"
        strokeOpacity={0.7}
        strokeWidth={1.5}
        strokeLinejoin="round"
      />

      {/* ── Score dots ── */}
      {scorePts.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={2.5} fill="currentColor" opacity={0.85} />
      ))}

      {/* ── Axis labels ── */}
      {data.map((d, i) => {
        const a = toRad(i, N);
        const lx = cx + labelOffset * Math.cos(a);
        const ly = cy + labelOffset * Math.sin(a);
        // Align labels based on which side of centre they land on.
        const cosA = Math.cos(a);
        const anchor =
          Math.abs(cosA) < 0.15 ? 'middle' : cosA > 0 ? 'start' : 'end';
        return (
          <text
            key={d.key}
            x={lx}
            y={ly}
            textAnchor={anchor}
            dominantBaseline="middle"
            fontSize={9}
            fill="currentColor"
            opacity={0.55}
            style={{
              fontFamily: 'var(--font-mono, ui-monospace, monospace)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            {d.label}
          </text>
        );
      })}
    </svg>
  );
}
